import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import useAreOrganizationSchemasCreatableQuery from 'auth/useAreOrganizationSchemasCreatableQuery';
import * as benchlingUtil from 'BenchlingUtil';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import notifications from 'notifications';
import {schemaFieldsetNamePluralized} from 'schemas/schema-interface/util/schemaFieldsetName';
import SchemasListing from 'schemas/SchemasListing';
import {SCHEMA_TYPES} from 'schemas/spec/SchemaConfigConstants';
import {capwords} from 'util/NameUtil';
import {withLoadingScreen} from 'util/Promises';

import {SchemaInterfacesPageQuery} from './__generated/SchemaInterfacesPageQuery';
import {
  bioentityTypeToHumanName,
  OptionalBaseBioentityType,
} from './SchemaInterfaceBaseBioentityTypeDropdown';
import {createSchemaInterfaceURL, schemaInterfaceURL, bulkArchive, bulkUnarchive} from './SchemaInterfaceDAO';

type SchemaInterface = SchemaInterfacesPageQuery['schemaInterfaceListing']['schemaInterfaces'][number];

const SCHEMA_INTERFACE_COLUMN_HEADERS = [
  {name: capwords(schemaFieldsetNamePluralized()), width: 4},
  {name: 'Organization', width: 4},
  {name: 'Entity Type', width: 4},
];

interface PresentationalSchemaInterfacesPageProps {
  schemaInterfaces: Array<SchemaInterface> | null;
  areOrganizationSchemasCreatable: boolean;
  refetch: () => void;
}

export class PresentationalSchemaInterfacesPage extends React.Component<PresentationalSchemaInterfacesPageProps> {
  private handleNavigateToCreate = (): void => {
    benchlingUtil.navigate(null, createSchemaInterfaceURL());
  };

  bulkArchiveSchemaInterfaces = async (ids: Array<string>, purpose: string): Promise<void> => {
    try {
      await withLoadingScreen(bulkArchive(ids, purpose), 'Archiving...');
      notifications.success('Items successfully archived.');
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      notifications.notifyError(e);
    }
    this.props.refetch();
  };

  bulkUnarchiveSchemaInterfaces = async (ids: Array<string>): Promise<void> => {
    try {
      await withLoadingScreen(bulkUnarchive(ids));
      notifications.success('Items successfully unarchived.');
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      notifications.notifyError(e);
    }
    this.props.refetch();
  };

  render(): JSX.Element {
    benchlingUtil.setWindowTitle(capwords(schemaFieldsetNamePluralized()));
    return (
      <SchemasListing
        schemas={this.props.schemaInterfaces}
        schemaType={SCHEMA_TYPES.SCHEMA_INTERFACE}
        columnHeaders={SCHEMA_INTERFACE_COLUMN_HEADERS}
        navigateToCreate={this.handleNavigateToCreate}
        urlForSchema={schemaInterfaceURL}
        archiveSchemas={this.bulkArchiveSchemaInterfaces}
        unarchiveSchemas={this.bulkUnarchiveSchemaInterfaces}
        isWritable={this.props.areOrganizationSchemasCreatable}
        mapSchemaToRowValue={(schemaInterface) => [
          <span>{schemaInterface.name}</span>,
          <span>{schemaInterface.organization.name}</span>,
          <span>
            {bioentityTypeToHumanName(schemaInterface.baseBioentityType as OptionalBaseBioentityType)}
          </span>,
        ]}
      />
    );
  }
}

export const SCHEMA_INTERFACES_PAGE_QUERY: DocumentNode = gql`
  query SchemaInterfacesPageQuery($paginationParameters: SequencePaginationParameters)
  @skipAudit
  @engComponent(name: SCHEMAS) {
    schemaInterfaceListing(paginationParameters: $paginationParameters) {
      schemaInterfaces {
        id
        name
        organization {
          id
          name
        }
        baseBioentityType {
          folderItemType
          sequenceType
        }
        ...HasArchiveRecordGraphQL
      }
      lastSeenApiId
    }
  }
  ${getFragmentWrapper('HasArchiveRecord').fragment}
`;

const SCHEMA_INTERFACES_PAGE_QUERY_LIMIT = 200;

export default function SchemaInterfacesPage(): JSX.Element {
  const areOrganizationSchemasCreatableResults = useAreOrganizationSchemasCreatableQuery();
  const areOrganizationSchemasCreatable =
    areOrganizationSchemasCreatableResults.queryResultType === QueryResultType.SUCCESS &&
    areOrganizationSchemasCreatableResults.areOrganizationSchemasCreatable;

  // TODO: BNCH-47113 update query to page through all results
  const queryResult = useBenchlingQuery<SchemaInterfacesPageQuery>(SCHEMA_INTERFACES_PAGE_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      paginationParameters: {
        limit: SCHEMA_INTERFACES_PAGE_QUERY_LIMIT,
        lastSeenApiId: null,
      },
    },
  });
  const schemaInterfaces =
    queryResult.type === QueryResultType.SUCCESS
      ? [...queryResult.data.schemaInterfaceListing.schemaInterfaces].sort((i1, i2) =>
          i1.name.toLowerCase().localeCompare(i2.name.toLowerCase())
        )
      : null;

  return (
    <PresentationalSchemaInterfacesPage
      schemaInterfaces={schemaInterfaces}
      areOrganizationSchemasCreatable={areOrganizationSchemasCreatable}
      refetch={queryResult.refetch}
    />
  );
}
